import type { OneTimeModalSlide as OneTimeModalSlideType } from '@backrs_ui/grow-ui/src/redux/oneTimeModal/oneTimeModal.types';

import { Box, Typography, Skeleton, SxProps, Theme } from '@mui/material';

type OneTimeModalSlideProps = Readonly<{
  slide: OneTimeModalSlideType;
  isImageLoaded: boolean;
}>;

export const OneTimeModalSlide = ({ slide, isImageLoaded }: OneTimeModalSlideProps) => {
  const mediaUrl = slide.media?.media_url;

  return (
    <Box sx={containerSx} aria-live="polite">
      {mediaUrl && (
        <Box sx={mediaSx}>
          {!isImageLoaded && <Skeleton variant="rectangular" sx={skeletonSx} animation="wave" />}
          <Box component="img" src={mediaUrl} alt={slide.title} sx={imageSx(isImageLoaded)} />
        </Box>
      )}

      <Box sx={textContentSx}>
        <Typography variant="text-lg-semibold">{slide.title}</Typography>

        <Typography
          variant="text-sm-regular"
          color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
        >
          {slide.body}
        </Typography>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  minHeight: 0,
  padding: backrs.spacing['spacing-3xl'],
  paddingBottom: 0,
  gap: backrs.spacing['spacing-2xl'],
});

const mediaSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  aspectRatio: '352 / 205',
  flex: '0 1 auto',
  minHeight: 0,
  maxHeight: '100%',
  overflow: 'hidden',
  borderRadius: backrs.spacing['spacing-md'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  position: 'relative',
});

const skeletonSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const imageSx = (isLoaded: boolean): SxProps<Theme> => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: isLoaded ? 1 : 0,
  transition: 'opacity 0.2s ease-in-out',
});

const textContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  textAlign: 'center',
});
